import styled, { css, keyframes } from 'styled-components';

import { SWReactIcons } from '@similarweb/icons';
import { colorsPalettes, rgba, setFontVariant } from '@similarweb/styles';

import { aiAgentColors } from 'constants/aiAgentColors';

import type { ButtonVariant } from './ButtonVariant';

const loadingAnimation = keyframes`
  0% {
    width: 0;
  }
  100% {
    width: 100%;
  }
`;

const getColorByVariant = (variant: string) => {
  switch (variant) {
    case 'primary':
      return {
        color: colorsPalettes.carbon[0],
      };
    case 'flat':
      return {
        color: colorsPalettes.blue[400],
        activeColor: colorsPalettes.blue[500],
        disabledColor: colorsPalettes.carbon[200],
      };
    case 'trial':
      return {
        color: colorsPalettes.carbon[0],
      };
    case 'flatTrial':
      return {
        color: colorsPalettes.indigo['300'],
      };
    case 'flatDanger':
      return {
        color: colorsPalettes.red['s100'],
        activeColor: colorsPalettes.red[500],
      };
    case 'flatDark':
      return {
        color: colorsPalettes.carbon[500],
      };
    case 'danger':
      return {
        color: colorsPalettes.carbon[0],
      };
    case 'ghost':
      return {
        color: colorsPalettes.blue[400],
        hoverColor: colorsPalettes.blue[500],
      };
    case 'ghostDark':
      return {
        color: colorsPalettes.carbon[500],
      };
    case 'ghostCarbon200':
      return {
        color: colorsPalettes.carbon[200],
      };
    case 'ghostCarbon300':
      return {
        color: colorsPalettes.carbon[300],
      };

    case 'ghostTrial':
      return {
        color: colorsPalettes.indigo[300],
      };
    case 'emailAgent':
      return {
        color: 'transparent',
      };
    default:
      return {};
  }
};

export const StyledButton = styled.button<{
  variant: ButtonVariant;
  isLoading?: boolean;
  isActive?: boolean;
  indicator?: boolean;
  isHoverDisabled?: boolean;
}>`
  padding: 8px 16px;
  ${setFontVariant('button', colorsPalettes.carbon[0])};
  border: none;
  cursor: ${({ isHoverDisabled }) => (isHoverDisabled ? 'default' : 'pointer')};
  border-radius: 18px;
  transition:
    background-color 0.2s ease-in-out,
    border-color 0.2s ease-in-out,
    color 0.2s ease-in-out;

  &:disabled {
    background-color: ${colorsPalettes.carbon[200]};
    cursor: not-allowed;
  }

  a {
    color: inherit;
    text-decoration: none;
  }

  ${({ indicator }) =>
    indicator &&
    css`
      position: relative;

      &::after {
        content: '';
        position: absolute;
        top: 0;
        right: 0;
        width: 8px;
        height: 8px;
        background-color: ${colorsPalettes.red[400]};
        border-radius: 50%;
      }
    `}

  ${({ isLoading }) =>
    isLoading &&
    css`
      overflow: hidden;
      position: relative;

      &:before {
        content: '';
        position: absolute;
        height: 100%;
        width: 0;
        top: 0;
        left: 0;
        background: rgba(22, 39, 93, 0.3);
        animation: ${loadingAnimation} 2s infinite;
      }
    `}

  ${({ variant, isHoverDisabled, isActive }) => {
    const colors = getColorByVariant(variant);
    return css`
      color: ${colors.color};

      ${!isHoverDisabled &&
      css`
        &:hover,
        &:active {
          color: ${colors.hoverColor};
        }
      `}

      ${isActive &&
      css`
        color: ${colors.hoverColor};
      `}

      &:active {
        color: ${colors.activeColor};
      }

      &:disabled {
        color: ${colors.disabledColor};
      }
    `;
  }}

  ${({ variant, isHoverDisabled, isActive }) => {
    switch (variant) {
      case 'primary':
        return css`
          background-color: ${colorsPalettes.blue[400]};

          &:hover,
          &:active {
            background-color: ${colorsPalettes.blue[500]};
          }

          ${isActive &&
          css`
            background-color: ${colorsPalettes.blue[500]};
          `}

          &:disabled {
            background-color: ${colorsPalettes.carbon[200]};
          }
        `;
      case 'flat':
        return css`
          background-color: ${colorsPalettes.carbon[0]};

          ${!isHoverDisabled &&
          css`
            &:hover {
              background-color: ${rgba(colorsPalettes.carbon[200], 0.2)};
            }

            &:active {
              background-color: ${colorsPalettes.blue[200]};
            }
          `}

          ${isActive &&
          css`
            background-color: ${rgba(colorsPalettes.carbon[200], 0.2)};
          `}

          &:disabled {
            background-color: ${colorsPalettes.carbon[0]};
          }
        `;
      case 'trial':
        return css`
          background-color: ${colorsPalettes.indigo[300]};

          ${!isHoverDisabled &&
          css`
            &:hover {
              background-color: ${colorsPalettes.indigo[400]};
            }
          `}

          ${isActive &&
          css`
            background-color: ${colorsPalettes.indigo[400]};
          `}
        `;
      case 'flatTrial':
        return css`
          background-color: transparent;

          ${!isHoverDisabled &&
          css`
            &:hover {
              background-color: ${rgba(colorsPalettes.carbon[200], 0.2)};
            }
          `}

          ${isActive &&
          css`
            background-color: ${rgba(colorsPalettes.carbon[200], 0.2)};
          `}
        `;
      case 'flatDanger':
        return css`
          background-color: ${colorsPalettes.carbon[0]};

          ${!isHoverDisabled &&
          css`
            &:hover,
            &:active {
              background-color: ${rgba(colorsPalettes.carbon[200], 0.2)};
            }
          `}

          ${isActive &&
          css`
            background-color: ${rgba(colorsPalettes.carbon[200], 0.2)};
          `}
        `;
      case 'flatDark':
        return css`
          background-color: ${colorsPalettes.carbon[0]};

          ${!isHoverDisabled &&
          css`
            &:hover {
              background-color: ${rgba(colorsPalettes.carbon[200], 0.2)};
            }
          `}

          ${isActive &&
          css`
            background-color: ${rgba(colorsPalettes.carbon[200], 0.2)};
          `}

          &:active {
            background-color: ${colorsPalettes.blue[200]};
          }
        `;
      case 'danger':
        return css`
          background-color: ${colorsPalettes.red['s100']};

          ${!isHoverDisabled &&
          css`
            &:hover,
            &:active {
              background-color: ${colorsPalettes.red[500]};
            }
          `}

          ${isActive &&
          css`
            background-color: ${colorsPalettes.red[500]};
          `}
        `;
      case 'ghost':
        return css`
          background-color: transparent;
          border: 1px solid ${colorsPalettes.blue[400]};

          ${!isHoverDisabled &&
          css`
            &:hover,
            &:active {
              border-color: ${colorsPalettes.blue[500]};
            }
          `}

          ${isActive &&
          css`
            border-color: ${colorsPalettes.blue[500]};
          `}

          &:active {
            background-color: ${colorsPalettes.blue[200]};
          }
        `;
      case 'ghostCarbon200':
      case 'ghostCarbon300':
        return css`
          background-color: transparent;
          border: none;

          ${!isHoverDisabled &&
          css`
            &:hover,
            &:active {
              background-color: ${rgba(colorsPalettes.carbon[200], 0.2)};
            }
          `}

          ${isActive &&
          css`
            background-color: ${rgba(colorsPalettes.carbon[200], 0.2)};
          `}
        `;
      case 'ghostDark':
        return css`
          background-color: transparent;
          border: 1px solid ${colorsPalettes.carbon[500]};

          ${!isHoverDisabled &&
          css`
            &:hover {
              background-color: ${rgba(colorsPalettes.carbon[200], 0.2)};
            }
          `}

          ${isActive &&
          css`
            background-color: ${rgba(colorsPalettes.carbon[200], 0.2)};
          `}

          &:active {
            background-color: ${colorsPalettes.blue[200]};
          }
        `;
      case 'ghostTrial':
        return css`
          background-color: transparent;
          border: 1px solid ${colorsPalettes.indigo[300]};

          &:hover {
            background-color: ${colorsPalettes.indigo[100]};
          }

          ${isActive &&
          css`
            background-color: ${colorsPalettes.indigo[100]};
          `}
        `;
      case 'emailAgent':
        return css`
          background-color: transparent;
          padding: 8px 16px;
          gap: 4px;
          display: flex;
          align-items: center;

          ${!isHoverDisabled &&
          css`
            &:hover {
              background-color: ${rgba(colorsPalettes.carbon[200], 0.2)};
            }
          `}

          ${isActive &&
          css`
            background-color: ${rgba(colorsPalettes.carbon[200], 0.2)};
          `}

          &:disabled {
            background-color: transparent;
            color: ${colorsPalettes.carbon[400]};
          }

          > span {
            background: linear-gradient(90deg, ${aiAgentColors.gradient.blue} 1%, ${aiAgentColors.gradient.green});
            -webkit-background-clip: text;
            -webkit-text-fill-color: transparent;
            background-clip: text;
            white-space: nowrap;
          }

          &:disabled > span {
            background: transparent;
            -webkit-text-fill-color: ${colorsPalettes.carbon[400]};
          }
        `;
    }
  }}
`;

export const StyledIconButton = styled(StyledButton)`
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 4px;

  ${({ variant, isActive, isHoverDisabled }) => {
    const colors = getColorByVariant(variant);
    return css`
      ${SWReactIcons} {
        svg path {
          fill: ${colors.color};
        }
      }

      ${!isHoverDisabled &&
      css`
        &:hover,
        &:active {
          ${SWReactIcons} {
            svg path {
              fill: ${colors.hoverColor || colors.color};
            }
          }
        }
      `}

      ${isActive &&
      css`
        ${SWReactIcons} {
          svg path {
            fill: ${colors.hoverColor || colors.color};
          }
        }
      `}

      &:disabled {
        ${SWReactIcons} {
          svg path {
            fill: ${colors.disabledColor || colorsPalettes.carbon[200]};
          }
        }
      }
    `;
  }}
`;

export const StyledGainAccessButton = styled(StyledButton)`
  display: inline-flex;
  gap: 5px;

  a {
    color: inherit;
    text-decoration: none;
  }
`;
